import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, SendIcon, CameraIcon, CheckCircle2Icon, Wand2Icon } from 'lucide-react';
export function SellFlow() {
  const [query, setQuery] = useState('');
  const [step, setStep] = useState(0);
  const [chatHistory, setChatHistory] = useState<{
    role: 'user' | 'ai';
    content: string | React.ReactNode;
  }[]>([{
    role: 'ai',
    content: 'Hi! What would you like to sell today? Just tell me what it is.'
  }]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    const userMsg = query;
    setQuery('');
    setChatHistory(prev => [...prev, {
      role: 'user',
      content: userMsg
    }]);
    if (step === 0) {
      setTimeout(() => {
        setChatHistory(prev => [...prev, {
          role: 'ai',
          content: "Great, a 2018 Prado. I've set up the basic category. Could you upload a few photos? I can help make them look professional."
        }]);
        setStep(1);
      }, 1000);
    }
  };
  const handlePhotoUpload = () => {
    setChatHistory(prev => [...prev, {
      role: 'user',
      content: <div className="flex gap-2" data-id="element-3120">
            <div className="w-20 h-20 bg-slate-200 rounded-lg overflow-hidden" data-id="element-3121">
              <img src="https://images.unsplash.com/photo-1590362891991-f776e747a588?auto=format&fit=crop&q=80&w=200" alt="Car" className="w-full h-full object-cover" data-id="element-3122" />
            </div>
            <div className="w-20 h-20 bg-slate-200 rounded-lg overflow-hidden" data-id="element-3123">
              <img src="https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=200" alt="Car" className="w-full h-full object-cover" data-id="element-3124" />
            </div>
          </div>
    }]);
    setTimeout(() => {
      setChatHistory(prev => [...prev, {
        role: 'ai',
        content: <div className="space-y-4" data-id="element-3125">
              <p data-id="element-3126">
                Thanks! I've analyzed the photos and drafted your listing. How
                does this look?
              </p>
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm text-slate-800" data-id="element-3127">
                <h4 className="font-bold text-lg mb-2" data-id="element-3128">
                  Toyota Land Cruiser Prado GXR 2018
                </h4>
                <p className="text-sm text-slate-600 mb-3" data-id="element-3129">
                  Excellent condition, original paint. Based on market data, I
                  suggest pricing it at AED 82,000.
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-3130">
                  <div className="bg-slate-50 p-2 rounded" data-id="element-3131">
                    Price: AED 82,000
                  </div>
                  <div className="bg-slate-50 p-2 rounded" data-id="element-3132">Specs: GCC</div>
                </div>
              </div>
              <div className="flex flex-col gap-2" data-id="element-3133">
                <button className="w-full py-2 bg-gradient-to-r from-primary-indigo to-ai-purple text-white rounded-lg font-medium flex items-center justify-center gap-2" data-id="element-3134">
                  <Wand2Icon className="w-4 h-4" data-id="element-3135" />
                  Make photos look professional (AI)
                </button>
                <button className="w-full py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors" data-id="element-3136">
                  Publish Listing
                </button>
              </div>
            </div>
      }]);
      setStep(2);
    }, 1500);
  };
  return <div className="flex-1 flex flex-col h-[calc(100vh-60px)] lg:h-screen bg-slate-50" data-id="element-3137">
      <div className="p-4 bg-white border-b border-slate-200 text-center sticky top-0 z-10" data-id="element-3138">
        <h1 className="font-display font-bold text-xl text-dark-navy" data-id="element-3139">
          Tell Talab what you want to sell
        </h1>
      </div>

      <div className="flex-1 overflow-y-auto p-4 lg:p-8" data-id="element-3140">
        <div className="max-w-2xl mx-auto w-full space-y-6 pb-20" data-id="element-3141">
          {chatHistory.map((msg, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-3142">
              {msg.role === 'ai' && <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center mr-3 flex-shrink-0 shadow-sm" data-id="element-3143">
                  <SparklesIcon className="w-4 h-4 text-white" data-id="element-3144" />
                </div>}
              <div className={`max-w-[85%] rounded-2xl px-5 py-3.5 shadow-sm ${msg.role === 'user' ? 'bg-primary-indigo text-white rounded-tr-none' : 'bg-white border border-slate-200 text-slate-700 rounded-tl-none'}`} data-id="element-3145">
                {msg.content}
              </div>
            </motion.div>)}
        </div>
      </div>

      <div className="bg-white border-t border-slate-200 p-4 sticky bottom-0 z-10" data-id="element-3146">
        <div className="max-w-2xl mx-auto w-full" data-id="element-3147">
          {step === 1 ? <div className="flex gap-3" data-id="element-3148">
              <button onClick={handlePhotoUpload} className="flex-1 py-4 bg-slate-100 border-2 border-dashed border-slate-300 rounded-xl text-slate-600 font-medium flex items-center justify-center gap-2 hover:bg-slate-50 hover:border-primary-indigo transition-all" data-id="element-3149">
                <CameraIcon className="w-5 h-5" data-id="element-3150" />
                Upload Photos
              </button>
            </div> : step === 0 ? <form onSubmit={handleSubmit} className="relative ai-glow" data-id="element-3151">
              <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="E.g., I want to sell my 2018 Prado" className="w-full px-6 py-4 pr-14 bg-white border-2 border-slate-200 rounded-2xl text-base focus:outline-none focus:border-primary-indigo focus:ring-4 focus:ring-primary-indigo/10 transition-all" data-id="element-3152" />
              <button type="submit" disabled={!query.trim()} className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 bg-gradient-to-r from-primary-indigo to-ai-purple text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50" data-id="element-3153">
                <SendIcon className="w-5 h-5" data-id="element-3154" />
              </button>
            </form> : null}
        </div>
      </div>
    </div>;
}